// Data Providers
import * as Sentry from "@sentry/vue";
import { ProjectWrapperType } from "@/dataProviders/ProjectWrapper";

// Types
import {
  UpdateProjectBaseInfoType,
  UpdateProjectCalculationAssumptionsType,
  EmissionFactorType,
} from "@/features/BuildingProject";

// Mirrors the same predicate in CreateBuildingProjectService — keep both in sync when this changes.
const hasMeaningfulSelection = (row: EmissionFactorType | null | undefined): boolean => {
  if (!row) return false;
  return Boolean(row.type) || Boolean(row.customSourceId) || Boolean(row.customSource);
};

// Utils
import {
  updateProjectActiveBranches,
  syncProjectIntegrations,
  updateProjectBaseInfo,
  updateProjectCalculationAssumptions,
} from "@/api/services/BuildingProject";
import { withoutIncompleteReferenceAreaEntries } from "../utils/referencedAreasPayload";
import { ProjectWriteUnavailableError } from "./projectWriteErrors";
import { isRailUnavailable, noteRailUnavailable, type ProjectWriteRail } from "./projectWriteAvailability";
import { updateProjectScalars, updateProjectStatus, type ProjectScalarInput } from "./projectScalarWrites";
import { replaceProjectModels } from "./projectModelWrites";
import { activeNamesOf, modelSignature, type ProjectModelFlag } from "./modelSignature";
import { replaceProjectReferenceAreas } from "./projectReferenceAreaWrites";
import { replaceProjectBuildingAreas } from "./projectBuildingAreaWrites";
import {
  buildingAreaRows,
  buildingAreasOf,
  heatAreaAsStored,
  isMixedUse,
  referencedAreasAsStored,
  type ProjectBuildingAreasWriteInput,
} from "./buildingAreaWriteInput";
import {
  upsertProjectCalculation,
  upsertProjectLocation,
  upsertProjectSpecialConditionConfig,
  upsertProjectSpeckleParameterConfig,
} from "./projectSetupWrites";
import type {
  ProjectCalculationInput,
  ProjectLocationWriteInput,
  ProjectSpecialConditionConfigInput,
  ProjectSpeckleParameterConfigInput,
} from "../types/ProjectSetupWriteTypes";
import type { ReferencedAreaEntryType } from "../types/ReferencedAreasType";
import { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import { useClassificationTypes } from "@/dataProviders/ClassificationTypes";
import { useReferenceAreaTypeOptions } from "@/dataProviders/ReferenceAreaTypeOptions";
import { useBuildingTypes } from "@/dataProviders/BuildingTypes";
import { BuildingProjectStageEnums } from "@/enums";
import { buildingTypeDisplayLabel } from "../utils/buildingTypeLabel";
import payloadChanged from "./payloadChanged";

type Project = ProjectWrapperType["project"];

// Payload builders — pure functions of the project, so payloadChanged compares what a write would
// actually send rather than a hand-listed field set that could silently fall out of sync.

const buildProjectScalars = (project: Project): ProjectScalarInput => ({
  projectId: project.id,
  name: project.projectName,
  projectNumber: project.projectNumber ?? null,
  clientName: project.clientName ?? null,
  builderName: project.builder ?? null,
  lcaResponsibleName: project.lcaResponsibleName ?? null,
  numberOfPeople: project.numberOfPeople ?? null,
  buildingTypeId: Number.parseInt(project.buildingTypeEnum as unknown as string),
  // `?? null`, not `|| null`: status 0 is "Competition", a real answer.
  projectStatusId: project.buildingProjectStageEnum ?? null,
  yearOfConstruction: project.emissionCalculationAssumptions.emissionMetaData.yearOfConstruction,
  yearOfCommissioning: project.emissionCalculationAssumptions.emissionMetaData.yearOfCommissioning,
});

const buildProjectBaseInfo = (project: Project): UpdateProjectBaseInfoType => ({
  projectName: project.projectName,
  // `?? ""`, not its three neighbours: UpsertProjectMutation's `$clientName` is a non-nullable
  // `String!`, so a project with no client name would 500 the whole document without this.
  clientName: project.clientName ?? "",
  builder: project.builder,
  buildingAddress: project.buildingAddress,
  lcaResponsibleName: project.lcaResponsibleName,
  numberOfPeople: project.numberOfPeople,
  type: Number.parseInt(project.buildingTypeEnum as unknown as string),
  stage: Number.parseInt(project.buildingProjectStageEnum as unknown as string),
  projectNumber: project.projectNumber,
  specialConditionConfig: project.specialConditionConfig,
  speckleConfiguration: project.speckleConfiguration,
});

const buildCalculationAssumptions = (project: Project): UpdateProjectCalculationAssumptionsType => {
  const { emissionCalculationAssumptions } = project;
  const { projectEnergyData } = emissionCalculationAssumptions;
  const { constructional, operational } = projectEnergyData;

  return {
    // Through `referencedAreasAsStored`/`heatAreaAsStored`: this PUT declares all six building-area
    // numbers non-nullable, but below-ground is optional. See those helpers for why the coercion is here.
    referencedAreas: referencedAreasAsStored(
      withoutIncompleteReferenceAreaEntries(emissionCalculationAssumptions.referencedAreas)
    ),
    calculationType: emissionCalculationAssumptions.calculationType,
    heatArea: heatAreaAsStored(emissionCalculationAssumptions.heatArea),
    wastagePercentage: emissionCalculationAssumptions.wastagePercentage,
    projectEnergyData: {
      ...projectEnergyData,
      // Note that constructional and operational are optional and should be set to undefined if they are not present (e.g. value is null)
      constructional: {
        electricity: hasMeaningfulSelection(constructional?.electricity) ? constructional?.electricity ?? null : null,
        earthMoved: hasMeaningfulSelection(constructional?.earthMoved) ? constructional?.earthMoved ?? null : null,
        heat: hasMeaningfulSelection(constructional?.heat) ? constructional?.heat ?? null : null,
        machineryFuel: hasMeaningfulSelection(constructional?.machineryFuel) ? constructional?.machineryFuel ?? null : null,
      },
      operational: {
        electricity: hasMeaningfulSelection(operational?.electricity) ? operational?.electricity ?? null : null,
        heat: hasMeaningfulSelection(operational?.heat) ? operational?.heat ?? null : null,
      },
    },
    metaDataRequest: {
      yearOfCommissioning: emissionCalculationAssumptions.emissionMetaData.yearOfCommissioning,
      yearOfConstruction: emissionCalculationAssumptions.emissionMetaData.yearOfConstruction,
      includedPhases: emissionCalculationAssumptions.emissionMetaData.includedPhases.map((phase) => String(phase)),
      includedPhasesTranslated: emissionCalculationAssumptions.emissionMetaData.includedPhasesTranslated,
    },
  };
};

// The calculation row: null while the project is half-initialised (no type or period), matching
// what the REST record carried.
const buildProjectCalculation = (project: Project): ProjectCalculationInput | null => {
  const { calculationType, emissionMetaData } = project.emissionCalculationAssumptions;
  if (calculationType.calculationTypeEnum == null || calculationType.considerationPeriod == null) return null;
  return {
    projectId: project.id,
    calculationTypeId: Number(calculationType.calculationTypeEnum),
    considerationPeriod: Number(calculationType.considerationPeriod),
    // 0 is how "no limit set" is stored; the column is NOT NULL (see ProjectCalculationInput).
    thresholdLimit: calculationType.thresholdLimitWithoutExtra ?? 0,
    installationThresholdLimit: calculationType.installationThresholdLimit ?? null,
    useDefaultEmissions: calculationType.useDefaultEmissions,
    stageIds: emissionMetaData.includedPhases.map(Number).sort((left, right) => left - right),
  };
};

// The named reference areas, one row each — the type-less rows the wizard keeps are not sent.
const buildReferenceAreaEntries = (project: Project): ReferencedAreaEntryType[] =>
  (withoutIncompleteReferenceAreaEntries(project.emissionCalculationAssumptions.referencedAreas).entries ?? []).map((entry) => ({
    type: entry.type,
    area: entry.area,
    description: entry.description ?? null,
  }));

// Built as the ROWS (not the six raw numbers): the columns are NOT NULL, so null→0 is not a real change. Its own
// rail (not the base-info PUT, which 404s on a `/base`-created project) — shares `buildingAreasOf` with the settings rail.
const buildBuildingAreaInput = (project: Project): ProjectBuildingAreasWriteInput => buildingAreasOf(project);

const buildBuildingAreaRows = (project: Project) => buildingAreaRows(buildBuildingAreaInput(project));

// What only the REST record can carry today: the wastage and the energy sources
// (project_operational, project_installation and their factors) — tables the user role can only read.
const buildRestOnlyAssumptions = (project: Project) => {
  const record = buildCalculationAssumptions(project);
  return {
    wastagePercentage: record.wastagePercentage,
    projectEnergyData: record.projectEnergyData,
  };
};

// The years reach the API through `updatecalculationassumptions`, not the base-info PUT — so the
// assumptions fallback must check them separately, or a year-only edit drops silently.
const buildProjectYears = (project: Project) => {
  const { emissionMetaData } = project.emissionCalculationAssumptions;
  return {
    yearOfConstruction: emissionMetaData.yearOfConstruction,
    yearOfCommissioning: emissionMetaData.yearOfCommissioning,
  };
};

const calculationTypeLabel = (calculationTypeId: number): string =>
  useCalculationTypes().getSelectedCalculationType(calculationTypeId)?.label ?? "";

const referenceAreaLabel = (typeKey: string): string =>
  useReferenceAreaTypeOptions()
    .getReferenceAreaTypeOptions()
    .find((option) => option.value === typeKey)?.label ?? typeKey;

// Resolved here, not in the write, because the trail prints values verbatim — an id alone would be meaningless. Falls back
// to the raw id when the store can't answer; relies on BuildingTypes being mounted (this save's only caller, ProjectSettingsPage).
const projectScalarLabels = {
  buildingType: (buildingTypeId: number): string => {
    const entry = useBuildingTypes()
      .getBuildingTypes()
      .find((option) => option.id === String(buildingTypeId));
    return entry ? buildingTypeDisplayLabel(entry.label) : String(buildingTypeId);
  },
  // getKeyFromValue answers "" for an id the enum does not know, and the enum's keys ARE the labels
  // the Project status select renders — the same call ProjectStatusModal makes for its own row.
  projectStatus: (projectStatusId: number): string =>
    BuildingProjectStageEnums.getKeyFromValue(projectStatusId) || String(projectStatusId),
};

const buildSpecialConditionConfig = (project: Project): ProjectSpecialConditionConfigInput => ({
  projectId: project.id,
  enabled: project.specialConditionConfig?.enabled ?? false,
});

// The address. Its rail already existed for the create wizard; the settings page never called it, so an
// address edit travelled only in the REST base-info PUT.
const buildProjectLocation = (project: Project): ProjectLocationWriteInput => ({
  projectId: project.id,
  street: project.buildingAddress?.street ?? "",
  streetNumber: project.buildingAddress?.streetNumber ?? "",
  postalCode: project.buildingAddress?.postalCode ?? "",
  city: project.buildingAddress?.city ?? "",
  country: project.buildingAddress?.country ?? "",
});

// The Speckle parameter mapping. Same story as the address, with no rail at all until now: a save that
// changed nothing else sent no write, and the refetch after it reverted the edit under a success toast.
const buildSpeckleParameterConfig = (project: Project): ProjectSpeckleParameterConfigInput => ({
  projectId: project.id,
  classificationTypeId: project.speckleConfiguration?.parameters?.classificationTypeId || null,
  classificationCode: project.speckleConfiguration?.parameters?.classificationCode ?? "",
});

const classificationTypeLabel = (classificationTypeId: string | null): string =>
  useClassificationTypes()
    .getClassificationTypes()
    .find((entry) => entry.classificationType?.id === classificationTypeId)?.classificationType?.shortName ?? "";

// No model builder of its own: `modelSignature` IS what this write sends, shared with the flow
// provider's baseline so the gate compares equivalent shapes. `UpdateModelsType` only survives in the REST fallback.

/** Saves the project, calling only the writes whose payload changed — the comparison is per WRITE, not per field.
 * `previousProject`/`previousModels` are the flow provider's last-saved snapshot, the same copy Discard restores from. */
const updateBuildingProject = async (
  projectWrapper: ProjectWrapperType,
  previousProject?: Project | null,
  previousModels?: ProjectWrapperType["models"] | null
) => {
  const { project, models } = projectWrapper;
  const buildingProjectId = project.id;
  const previous = previousProject ?? null;

  // Hasura first: the PUT 500s on a `/base`-created project (replays a nonexistent Marten aggregate).
  // ONE shared fallback flag, not one per rail — the PUT resends the whole record either way.
  let needsBaseInfoFallback = false;

  /** Runs a rail, and answers whether it carried the change. A refused rail is not retried — see projectWriteAvailability. */
  const railCarriedIt = async (rail: ProjectWriteRail, write: () => Promise<unknown>): Promise<boolean> => {
    if (isRailUnavailable(rail)) return false;

    try {
      await write();
      return true;
    } catch (writeError: unknown) {
      // Only the "this environment cannot write these columns" signature falls through to REST. Any
      // other failure is a real one and must not be papered over with a second write.
      if (!(writeError instanceof ProjectWriteUnavailableError)) throw writeError;

      noteRailUnavailable(rail, writeError);
      return false;
    }
  };

  const scalars = buildProjectScalars(project);
  const previousScalars = previous && buildProjectScalars(previous);
  if (payloadChanged(scalars, previousScalars)) {
    // The baseline travels with the write so the trail row can name which fields moved and what each
    // moved FROM, instead of listing all of them every time.
    const write = () => updateProjectScalars(scalars, { previous: previousScalars, labels: projectScalarLabels });
    if (!(await railCarriedIt("projectRow", write))) needsBaseInfoFallback = true;
  }

  // Needs its own comparison, not just its own write: it's not in `buildProjectScalars`, so a save that
  // only flips this toggle would compare equal there and write nothing.
  const specialCondition = buildSpecialConditionConfig(project);
  if (payloadChanged(specialCondition, previous && buildSpecialConditionConfig(previous))) {
    const write = () => upsertProjectSpecialConditionConfig(specialCondition);
    if (!(await railCarriedIt("specialConditionConfig", write))) needsBaseInfoFallback = true;
  }

  // No PUT behind these two: their tables are written by Hasura alone, and the PUT's handler replays the
  // whole aggregate for what is a change to one row. A missing permission surfaces as a failed save.
  const location = buildProjectLocation(project);
  const previousLocation = previous && buildProjectLocation(previous);
  if (payloadChanged(location, previousLocation)) {
    // The baseline travels with the write so the trail row can say what the address moved FROM,
    // leaving out fields that didn't move.
    await upsertProjectLocation(location, { operation: "update", previous: previousLocation });
  }

  const speckleParameters = buildSpeckleParameterConfig(project);
  if (payloadChanged(speckleParameters, previous && buildSpeckleParameterConfig(previous))) {
    const entityLabel = classificationTypeLabel(speckleParameters.classificationTypeId);
    await upsertProjectSpeckleParameterConfig(speckleParameters, { entityLabel });
  }

  // A mixed-use project's areas are a pair of rows per use, which only Hasura can write: the PUT carries
  // totals and leaves such a project's rows alone. So they go here, ahead of any PUT, with no fallback.
  const buildingAreas = buildBuildingAreaRows(project);
  const buildingAreasChanged = payloadChanged(buildingAreas, previous && buildBuildingAreaRows(previous));
  const mixedUse =
    isMixedUse(buildBuildingAreaInput(project).uses) || (previous ? isMixedUse(buildBuildingAreaInput(previous).uses) : false);
  if (buildingAreasChanged && mixedUse) {
    await replaceProjectBuildingAreas(buildBuildingAreaInput(project), {
      previous: previous && buildBuildingAreaInput(previous),
      labelFor: projectScalarLabels.buildingType,
    });
  }

  if (needsBaseInfoFallback) {
    // Diffed on its own payload, not inherited from the rails' verdicts — the rails and this PUT carry
    // different field sets. A missing snapshot still means "send it": `payloadChanged(x, null)` is true by design.
    const baseInfo = buildProjectBaseInfo(project);
    if (payloadChanged(baseInfo, previous && buildProjectBaseInfo(previous))) {
      await updateProjectBaseInfo(buildingProjectId, baseInfo);
    }
  }

  // The REST PUT overwrites the calculation row, areas, energy sources and years together, but Hasura can
  // write only some — so the PUT goes when a PUT-only field changed (or no baseline), else each part rails.
  const restOnly = buildRestOnlyAssumptions(project);
  let needsAssumptionsFallback = payloadChanged(restOnly, previous && buildRestOnlyAssumptions(previous));

  // The years, only when their own rail refused them — see `buildProjectYears`. Normally this never
  // fires; a year-only edit no longer drags in the PUT that 404s on a `/base`-created project.
  if (needsBaseInfoFallback && payloadChanged(buildProjectYears(project), previous && buildProjectYears(previous))) {
    needsAssumptionsFallback = true;
  }

  if (!needsAssumptionsFallback) {
    const calculation = buildProjectCalculation(project);
    if (payloadChanged(calculation, previous && buildProjectCalculation(previous))) {
      if (calculation === null) {
        needsAssumptionsFallback = true;
      } else {
        const label = calculationTypeLabel(calculation.calculationTypeId);
        const write = () => upsertProjectCalculation(calculation, { operation: "update", entityLabel: label });
        if (!(await railCarriedIt("projectCalculation", write))) needsAssumptionsFallback = true;
      }
    }

    const referenceAreas = buildReferenceAreaEntries(project);
    if (payloadChanged(referenceAreas, previous && buildReferenceAreaEntries(previous))) {
      const write = () =>
        replaceProjectReferenceAreas(
          { projectId: buildingProjectId, entries: referenceAreas },
          { previousEntries: previous && buildReferenceAreaEntries(previous), labelFor: referenceAreaLabel }
        );
      if (!(await railCarriedIt("projectReferenceAreas", write))) needsAssumptionsFallback = true;
    }

    // The building areas: same terms as the reference areas beside them — the PUT behind this rail is the one that was
    // failing to save building areas outright, a last resort for a missing permission, not a routine path.
    if (buildingAreasChanged && !mixedUse) {
      const write = () =>
        replaceProjectBuildingAreas(buildBuildingAreaInput(project), {
          previous: previous && buildBuildingAreaInput(previous),
          labelFor: projectScalarLabels.buildingType,
        });
      if (!(await railCarriedIt("projectBuildingAreas", write))) needsAssumptionsFallback = true;
    }
  }

  if (needsAssumptionsFallback) {
    const assumptions = buildCalculationAssumptions(project);
    if (payloadChanged(assumptions, previous && buildCalculationAssumptions(previous))) {
      await updateProjectCalculationAssumptions(buildingProjectId, assumptions);
    }
  }

  // Diffed against its own baseline: models live on the wrapper, not the project, so sending regardless would log a
  // spurious "Models updated" row every save — a staged removal is a plain omission (runSave clears its model first).
  const modelFlags = modelSignature(models);
  const previousModelFlags = previousModels ? modelSignature(previousModels) : null;
  if (payloadChanged(modelFlags, previousModelFlags)) {
    await saveModels(buildingProjectId, models, modelFlags, previousModelFlags, project.speckleData?.streamId);
  }
};

/** Works without a stream; with one, sync is nudged right after (best-effort). REST UpdateBranch is the fallback, and only works with a stream. */
const saveModels = async (
  buildingProjectId: string,
  models: ProjectWrapperType["models"],
  modelFlags: ProjectModelFlag[],
  previousModelFlags: ProjectModelFlag[] | null,
  streamId: string | undefined
) => {
  let unavailable: ProjectWriteUnavailableError | null = null;
  if (!isRailUnavailable("projectModels")) {
    try {
      await replaceProjectModels({ projectId: buildingProjectId, models: modelFlags, previousModels: previousModelFlags });
      if (streamId) {
        try {
          await syncProjectIntegrations(buildingProjectId);
        } catch (syncError: unknown) {
          Sentry.captureException(syncError);
        }
      }
      return;
    } catch (writeError: unknown) {
      if (!(writeError instanceof ProjectWriteUnavailableError)) throw writeError;
      noteRailUnavailable("projectModels", writeError);
      unavailable = writeError;
    }
  }
  if (!streamId) throw unavailable ?? new ProjectWriteUnavailableError();
  // Names only, and only the active ones: that is all this endpoint takes.
  await updateProjectActiveBranches(buildingProjectId, activeNamesOf(modelFlags));

  // REST UpdateBranch rebuilds the set from the Speckle listing plus the Marten mirror, so it can't express an add or a
  // remove — this guard throws instead of reporting false success; runs after the PUT so flag changes on existing rows still land.
  if (models.some((model) => !model.id || model.pendingRemoval)) throw unavailable ?? new ProjectWriteUnavailableError();
};

// The dashboard's status dialog. One column through the project-row rail; the REST PUT (resending the whole
// base-info record) only where the permission isn't deployed. Shares `buildProjectBaseInfo` with the save above.
export const updateProjectStage = async (
  project: Project,
  stage: number,
  labels: { statusLabel: string; previousStatusLabel: string | null } = { statusLabel: String(stage), previousStatusLabel: null }
) => {
  if (!isRailUnavailable("projectRow")) {
    try {
      await updateProjectStatus({ projectId: project.id, projectStatusId: stage, ...labels });
      return;
    } catch (writeError: unknown) {
      if (!(writeError instanceof ProjectWriteUnavailableError)) throw writeError;
      noteRailUnavailable("projectRow", writeError);
    }
  }
  return updateProjectBaseInfo(project.id, { ...buildProjectBaseInfo(project), stage });
};

export default updateBuildingProject;
